import { useEffect, useState } from 'react';
import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  REASON_MAX_LENGTH,
  REASON_MIN_LENGTH,
  TRANSITION_COMMENT_MAX_LENGTH,
} from '@complan/contracts';
import type { InitiativeStatus } from '@complan/domain';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';
import { TextArea } from '@/features/initiative/intake/fields';
import { STATUS_LABEL_KEYS } from '@/shared/i18n/enum-labels';

/**
 * The confirmation for a lifecycle status transition other than cancellation
 * (Submit, return to Draft, move to Planned, Approve, move back to Planned, make
 * Final — R9.2, R9.12, R9.13). Cancellation keeps its own dialog
 * ({@link ./CancelInitiativeDialog}) because its copy and destructive styling
 * differ; both drive the same transitions endpoint.
 *
 * Two text inputs, matching what R9 uses each for:
 *
 * - **Reason** is required by the transition table for every transition except
 *   Draft → Submitted (R9.13), 1 to 500 characters trimmed. `requiresReason`
 *   says whether this target needs one; when it does not, the field is hidden and
 *   no reason is sent. The server is the final authority and re-checks this.
 * - **Comment** is always optional, up to 1,000 characters (R9.2).
 *
 * The confirm button is disabled until a required reason is valid, so the common
 * rejection (missing reason) is prevented in the form rather than surfaced as a
 * round-trip error. A true dialog (focus trap, focus restore, `Esc`) via
 * {@link AriaModalDialog}.
 */
export function TransitionDialog({
  isOpen,
  targetStatus,
  requiresReason,
  isSubmitting,
  hasError,
  onConfirm,
  onClose,
}: {
  readonly isOpen: boolean;
  /** The status this transition moves the Initiative to; `null` while no transition is pending. */
  readonly targetStatus: InitiativeStatus | null;
  readonly requiresReason: boolean;
  readonly isSubmitting: boolean;
  readonly hasError: boolean;
  readonly onConfirm: (payload: { reason: string | null; comment: string | null }) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  // Clear both fields whenever the dialog opens for a new target, so a previous
  // attempt's text never carries into the next transition.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setComment('');
    }
  }, [isOpen, targetStatus]);

  const trimmedReasonLength = reason.trim().length;
  const reasonValid =
    !requiresReason ||
    (trimmedReasonLength >= REASON_MIN_LENGTH && reason.length <= REASON_MAX_LENGTH);
  const canConfirm = reasonValid && comment.length <= TRANSITION_COMMENT_MAX_LENGTH;

  const targetLabel = targetStatus === null ? '' : t(STATUS_LABEL_KEYS[targetStatus]);

  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={t('detail.transitionDialog.title', { status: targetLabel })}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('detail.transitionDialog.title', { status: targetLabel })}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">
        {t('detail.transitionDialog.body', { status: targetLabel })}
      </p>

      {hasError ? (
        <p className="mt-2 text-[12px] text-[#8E2A1E]">{t('detail.transitionDialog.error')}</p>
      ) : null}

      {requiresReason ? (
        <div className="mt-3 flex flex-col gap-1">
          <label htmlFor="transition-reason" className="text-[11.5px] font-semibold text-txt">
            {t('detail.transitionDialog.reasonLabel')}
          </label>
          <TextArea
            id="transition-reason"
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => setReason(event.target.value)}
          />
          <p className="text-muted-foreground text-[11px]">
            {t('detail.transitionDialog.reasonHint')}
          </p>
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor="transition-comment" className="text-[11.5px] font-semibold text-txt">
          {t('detail.transitionDialog.commentLabel')}
        </label>
        <TextArea
          id="transition-comment"
          value={comment}
          maxLength={TRANSITION_COMMENT_MAX_LENGTH}
          onChange={(event) => setComment(event.target.value)}
        />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
          {t('detail.transitionDialog.cancel')}
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!canConfirm || isSubmitting}
          onClick={() =>
            onConfirm({
              reason: requiresReason ? reason.trim() : null,
              comment: comment.trim() === '' ? null : comment.trim(),
            })
          }
        >
          {isSubmitting
            ? t('detail.transitionDialog.confirming')
            : t('detail.transitionDialog.confirm', { status: targetLabel })}
        </Button>
      </div>
    </AriaModalDialog>
  );
}
